import { useCallback, useEffect, useRef, useState } from 'react';
import { useProviderScheduleBulkMutation } from './useProviderSchedule';

const DEBOUNCE_MS = 800;

/**
 * `isBlocked` scopes every flush to the active schedule_type's row kind
 * (allow for CLOSED/STRICT, block for OPEN). The consumer is responsible
 * for flushing the pending buffer (via `flushNow`) before changing
 * schedule_type, otherwise a queued add/remove would land in the wrong
 * bucket.
 */
export const useDebouncedScheduleSave = ({ isBlocked }: { isBlocked: boolean }) => {
  const [pendingAdds, setPendingAdds] = useState<Set<string>>(new Set());
  const [pendingRemoves, setPendingRemoves] = useState<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mutation = useProviderScheduleBulkMutation();

  // Refs capture the latest pending sets for use inside the timer callback
  // without re-binding the timer on every state change.
  const latestAddsRef = useRef(pendingAdds);
  const latestRemovesRef = useRef(pendingRemoves);
  const latestIsBlockedRef = useRef(isBlocked);
  latestAddsRef.current = pendingAdds;
  latestRemovesRef.current = pendingRemoves;
  latestIsBlockedRef.current = isBlocked;

  const flushNow = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const adds = Array.from(latestAddsRef.current);
    const removes = Array.from(latestRemovesRef.current);
    if (adds.length === 0 && removes.length === 0) return;

    mutation.mutate(
      { is_blocked: latestIsBlockedRef.current, add: adds, remove: removes },
      {
        onSuccess: () => {
          // Only clear the dates that were part of THIS mutation payload.
          // Dates the user toggled WHILE the request was in flight stay in
          // the pending sets so they go out in the next debounce flush.
          // Clearing the whole Set (the previous behavior) caused fast
          // mobile tap bursts to "lose" the cells tapped between
          // mutate-call and mutate-response: those cells were in
          // pendingAdds but not in the in-flight payload, and the server
          // response wiped them before they could be saved.
          setPendingAdds((prev) => {
            const next = new Set(prev);
            adds.forEach((iso) => next.delete(iso));
            return next.size === prev.size ? prev : next;
          });
          setPendingRemoves((prev) => {
            const next = new Set(prev);
            removes.forEach((iso) => next.delete(iso));
            return next.size === prev.size ? prev : next;
          });
        },
        // On error we keep the pending sets so the calling component can
        // surface a retry option (toast wired by the consumer).
      }
    );
  }, [mutation]);

  const scheduleFlush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushNow, DEBOUNCE_MS);
  }, [flushNow]);

  const enqueueAdd = useCallback(
    (dateISO: string) => {
      setPendingRemoves((prev) => {
        if (!prev.has(dateISO)) return prev;
        const next = new Set(prev);
        next.delete(dateISO);
        return next;
      });
      setPendingAdds((prev) => {
        if (prev.has(dateISO)) return prev;
        const next = new Set(prev);
        next.add(dateISO);
        return next;
      });
      scheduleFlush();
    },
    [scheduleFlush]
  );

  const enqueueRemove = useCallback(
    (dateISO: string) => {
      setPendingAdds((prev) => {
        if (!prev.has(dateISO)) return prev;
        const next = new Set(prev);
        next.delete(dateISO);
        return next;
      });
      setPendingRemoves((prev) => {
        if (prev.has(dateISO)) return prev;
        const next = new Set(prev);
        next.add(dateISO);
        return next;
      });
      scheduleFlush();
    },
    [scheduleFlush]
  );

  // Flush on unmount so in-flight edits aren't lost on navigation.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        flushNow();
      }
    };
  }, [flushNow]);

  return {
    pendingAdds,
    pendingRemoves,
    enqueueAdd,
    enqueueRemove,
    flushNow,
    isSaving: mutation.isLoading,
    saveError: mutation.error as Error | null,
  };
};
